import { BesleyiciConfig, BesleyiciSekil } from '../FikoSPHParametreler';
import { ISPHEleman, ElemanTipi, olusturId } from './ISPHEleman';
import { Vector3 } from 'three';

/**
 * FikoBesleyici - Feeder/riser element for SPH fluid simulation
 * GPU struct size: 64 bytes
 *
 * Layout:
 * - pozX, pozY, pozZ, baglantiBoyutu (16 bytes)
 * - boyutX, boyutY, boyutZ, hacim (16 bytes)
 * - sekil as u32, oncelik as u32, aktif as u32, pad (16 bytes)
 * - reserved (16 bytes)
 */
export class FikoBesleyici implements ISPHEleman {
  readonly id: string;
  readonly tip: ElemanTipi = ElemanTipi.BESLEYICI;

  pozisyon: Vector3;
  sekil: BesleyiciSekil;
  boyutlar: Vector3;
  baglantiBoyutu: number;
  hacim: number;
  oncelik: number;
  aktif: boolean;
  ad: string;

  constructor(config: Partial<BesleyiciConfig> = {}) {
    this.id = config.id || olusturId();

    this.pozisyon = config.pozisyon ? new Vector3(config.pozisyon.x, config.pozisyon.y, config.pozisyon.z) : new Vector3(0, 0, 0);
    this.sekil = config.sekil ?? BesleyiciSekil.SILINDIR;
    // BesleyiciConfig.boyutlar is {cap?, yukseklik?, genislik?, derinlik?}, convert to Vector3
    const cap = config.boyutlar?.cap ?? 0.05;
    const yukseklik = config.boyutlar?.yukseklik ?? 0.1;
    const genislik = config.boyutlar?.genislik ?? 0.05;
    const derinlik = config.boyutlar?.derinlik ?? 0.05;
    this.boyutlar = new Vector3(cap || genislik, yukseklik, derinlik);
    this.baglantiBoyutu = config.baglantiBoyutu ?? 0.02;
    this.oncelik = config.oncelik ?? 5;
    this.aktif = config.aktif ?? true;
    this.ad = config.ad || 'Besleyici';

    // Calculate volume based on shape
    this.hacim = config.hacim ?? this.hesaplaHacim();
  }

  /**
   * Calculate volume based on shape and dimensions
   */
  private hesaplaHacim(): number {
    switch (this.sekil) {
      case BesleyiciSekil.SILINDIR:
        // Cylinder: π * r² * h (boyutlar.x = radius, boyutlar.y = height)
        return Math.PI * this.boyutlar.x * this.boyutlar.x * this.boyutlar.y;

      case BesleyiciSekil.KURE:
        // Sphere: (4/3) * π * r³ (boyutlar.x = radius)
        return (4/3) * Math.PI * Math.pow(this.boyutlar.x, 3);

      case BesleyiciSekil.DIKDORTGEN:
        // Box: width * height * depth
        return this.boyutlar.x * this.boyutlar.y * this.boyutlar.z;

      default:
        return 0;
    }
  }

  toGPUBytes(): ArrayBuffer {
    const buffer = new ArrayBuffer(64);
    const view = new DataView(buffer);
    let offset = 0;

    // Chunk 1: pozX, pozY, pozZ, baglantiBoyutu (16 bytes)
    view.setFloat32(offset, this.pozisyon.x, true); offset += 4;
    view.setFloat32(offset, this.pozisyon.y, true); offset += 4;
    view.setFloat32(offset, this.pozisyon.z, true); offset += 4;
    view.setFloat32(offset, this.baglantiBoyutu, true); offset += 4;

    // Chunk 2: boyutX, boyutY, boyutZ, hacim (16 bytes)
    view.setFloat32(offset, this.boyutlar.x, true); offset += 4;
    view.setFloat32(offset, this.boyutlar.y, true); offset += 4;
    view.setFloat32(offset, this.boyutlar.z, true); offset += 4;
    view.setFloat32(offset, this.hacim, true); offset += 4;

    // Chunk 3: sekil as u32, oncelik as u32, aktif as u32, pad (16 bytes)
    // Map string enum to numeric for GPU
    const sekilNumeric = this.sekil === BesleyiciSekil.SILINDIR ? 0 : this.sekil === BesleyiciSekil.KURE ? 1 : 2;
    view.setUint32(offset, sekilNumeric, true); offset += 4;
    view.setUint32(offset, this.oncelik, true); offset += 4;
    view.setUint32(offset, this.aktif ? 1 : 0, true); offset += 4;
    view.setUint32(offset, 0, true); offset += 4; // pad

    // Chunk 4: reserved (16 bytes)
    for (let i = 0; i < 4; i++) {
      view.setUint32(offset, 0, true);
      offset += 4;
    }

    return buffer;
  }

  gpuStructBoyut(): number {
    return 64;
  }

  toJSON(): any {
    return {
      id: this.id,
      tip: this.tip,
      pozisyon: { x: this.pozisyon.x, y: this.pozisyon.y, z: this.pozisyon.z },
      sekil: this.sekil,
      boyutlar: {
        cap: this.boyutlar.x,
        yukseklik: this.boyutlar.y,
        genislik: this.boyutlar.x,
        derinlik: this.boyutlar.z
      },
      baglantiBoyutu: this.baglantiBoyutu,
      hacim: this.hacim,
      oncelik: this.oncelik,
      aktif: this.aktif,
      ad: this.ad
    };
  }

  dogrula(): { gecerli: boolean; hatalar: string[] } {
    const hatalar: string[] = [];

    if (this.boyutlar.x <= 0 || this.boyutlar.y <= 0 || this.boyutlar.z <= 0) {
      hatalar.push('Boyutlar pozitif olmalıdır');
    }

    if (this.baglantiBoyutu <= 0) {
      hatalar.push('Bağlantı boyutu pozitif olmalıdır');
    }

    if (this.hacim <= 0) {
      hatalar.push('Hacim pozitif olmalıdır');
    }

    if (this.oncelik < 0 || this.oncelik > 10) {
      hatalar.push('Öncelik 0-10 arasında olmalıdır');
    }

    // Shape-specific validation
    switch (this.sekil) {
      case BesleyiciSekil.SILINDIR:
        if (this.boyutlar.x <= 0 || this.boyutlar.y <= 0) {
          hatalar.push('Silindir için yarıçap ve yükseklik pozitif olmalıdır');
        }
        break;

      case BesleyiciSekil.KURE:
        if (this.boyutlar.x <= 0) {
          hatalar.push('Küre için yarıçap pozitif olmalıdır');
        }
        break;

      case BesleyiciSekil.DIKDORTGEN:
        if (this.boyutlar.x <= 0 || this.boyutlar.y <= 0 || this.boyutlar.z <= 0) {
          hatalar.push('Dikdörtgen prizma için tüm boyutlar pozitif olmalıdır');
        }
        break;
    }

    // Validate volume calculation
    const hesaplananHacim = this.hesaplaHacim();
    const hacimFarki = Math.abs(this.hacim - hesaplananHacim);
    if (hacimFarki > 0.001) {
      hatalar.push(`Hacim tutarsızlığı: ${this.hacim.toFixed(6)} ≠ ${hesaplananHacim.toFixed(6)}`);
    }

    return {
      gecerli: hatalar.length === 0,
      hatalar
    };
  }

  klonla(): FikoBesleyici {
    return new FikoBesleyici({
      pozisyon: this.pozisyon.clone(),
      sekil: this.sekil,
      boyutlar: {
        cap: this.boyutlar.x,
        yukseklik: this.boyutlar.y,
        genislik: this.boyutlar.x,
        derinlik: this.boyutlar.z
      },
      baglantiBoyutu: this.baglantiBoyutu,
      hacim: this.hacim,
      oncelik: this.oncelik,
      aktif: this.aktif,
      ad: this.ad + ' (Kopya)'
    });
  }

  parametreListesi(): string[] {
    return [
      'pozisyon', 'sekil', 'boyutlar', 'baglantiBoyutu',
      'hacim', 'oncelik', 'aktif', 'ad'
    ];
  }

  parametreAyarla(anahtar: string, deger: any): boolean {
    switch (anahtar) {
      case 'pozisyon':
      case 'boyutlar':
        if (deger instanceof Vector3) {
          (this as any)[anahtar].copy(deger);
          if (anahtar === 'boyutlar') {
            this.hacim = this.hesaplaHacim();
          }
          return true;
        }
        break;
      case 'baglantiBoyutu':
      case 'hacim':
        if (typeof deger === 'number') {
          (this as any)[anahtar] = deger;
          return true;
        }
        break;
      case 'oncelik':
        if (typeof deger === 'number' && deger >= 0 && deger <= 10) {
          this.oncelik = deger;
          return true;
        }
        break;
      case 'sekil':
        // Accept both string and number for enum assignment
        (this as any).sekil = deger;
        this.hacim = this.hesaplaHacim();
        return true;
      case 'aktif':
        if (typeof deger === 'boolean') {
          this.aktif = deger;
          return true;
        }
        break;
      case 'ad':
        if (typeof deger === 'string') {
          this.ad = deger;
          return true;
        }
        break;
    }
    return false;
  }

  /**
   * Get a parameter value by name
   */
  parametreAl(anahtar: string): any {
    return (this as any)[anahtar];
  }

  /**
   * Recalculate volume based on current dimensions
   */
  hacmiYenidenHesapla(): void {
    this.hacim = this.hesaplaHacim();
  }

  /**
   * Get recommended connection size based on volume
   */
  onerileBaglantiBoyutu(): number {
    // Connection should be ~10-15% of the main dimension
    const maxBoyut = Math.max(this.boyutlar.x, this.boyutlar.y, this.boyutlar.z);
    return maxBoyut * 0.12;
  }
}

/**
 * Factory function to create default feeder element
 */
export function varsayilanBesleyici(config?: Partial<BesleyiciConfig>): FikoBesleyici {
  return new FikoBesleyici(config);
}
